import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, CircularProgress } from '@mui/material';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import {
  PyrenzDropzone,
  PyrenzModal,
  PyrenzModalContent,
  PyrenzBlueButton,
} from '@client/theme';
import { usePyrenzAlert } from '@renderer/provider';
import { uploadImage } from '@client/utility';

interface GalleryModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (urls: string[]) => void;
  bucketName?: string;
}

export function GalleryModal(props: GalleryModalProps) {
  const { open, onClose, onSubmit, bucketName = 'gallery' } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const showAlert = usePyrenzAlert();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: true,
  });

  async function handleUpload() {
    if (files.length === 0) return;
    setUploading(true);
    const urls: string[] = [];
    try {
      for (const file of files) {
        const result = await uploadImage(bucketName, file);
        if (result.url) urls.push(result.url);
      }
      setUploadedUrls(urls);
      showAlert('Images uploaded successfully!', 'success');
    } catch (err) {
      console.error(err);
      showAlert('Failed to upload images.', 'error');
    } finally {
      setUploading(false);
    }
  }

  function handleSubmit() {
    if (uploadedUrls.length > 0) {
      onSubmit(uploadedUrls);
      setFiles([]);
      setUploadedUrls([]);
      onClose();
    } else {
      showAlert('No uploaded images to submit.', 'alert');
    }
  }

  return (
    <PyrenzModal open={open} onClose={onClose} closeAfterTransition>
      <PyrenzModalContent
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Upload Gallery Images
        </Typography>
        <PyrenzDropzone {...getRootProps()}>
          <input {...getInputProps()} />
          <InsertPhotoOutlinedIcon sx={{ fontSize: 40 }} />
          <Typography>
            {isDragActive
              ? 'Drop images here...'
              : 'Drag & drop images, or click to select'}
          </Typography>
        </PyrenzDropzone>

        {files.length > 0 && (
          <Box
            sx={{
              mt: 2,
              maxHeight: 200,
              overflowY: 'auto',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {files.map((file, idx) => (
              <Box
                key={idx}
                sx={{
                  width: 80,
                  height: 80,
                  position: 'relative',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <PyrenzBlueButton
            variant="outlined"
            onClick={onClose}
            disabled={uploading}
          >
            Cancel
          </PyrenzBlueButton>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <PyrenzBlueButton
              variant="contained"
              onClick={handleUpload}
              disabled={uploading || files.length === 0}
            >
              {uploading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Upload'
              )}
            </PyrenzBlueButton>

            <PyrenzBlueButton
              variant="contained"
              onClick={handleSubmit}
              disabled={uploadedUrls.length === 0 || uploading}
            >
              Submit
            </PyrenzBlueButton>
          </Box>
        </Box>
      </PyrenzModalContent>
    </PyrenzModal>
  );
}
